import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { Checkbox } from '@ui/components/Checkbox'
import { Modal } from '@ui/components/Modal'

import { AccountDetailedDto, PhoneNumberAddValues } from '@model/AccountDetailedDto'
import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'
import { CountryPreferenceStrippedDto } from '@model/CountryDto'

import { useClientPhoneNumberValidation } from '@hooks/useClientPhoneNumberValidation'

import { PhoneNumberInput } from '@shared/components/PhoneNumberInput/PhoneNumberInput'

interface PhoneNumberAddModalProps {
  account: AccountDetailedDto
  onConfirm: (values: PhoneNumberAddValues) => void
  countries: CountryPreferenceStrippedDto[]
}

export const PhoneNumberAddModal: BaseModalType<PhoneNumberAddModalProps> = ({
  close,
  onConfirm,
  account,
  countries,
}) => {
  const { t } = useTranslation()

  const getInitialValues = (): PhoneNumberAddValues => {
    const residenceCountry = account?.addresses.find((x) => x.isPrimary)?.country
    const defaultCountry = countries.find(
      (country) => country.country.name === residenceCountry?.name
    )

    const countryPhoneCode = defaultCountry?.phoneCode || '+1'
    const countryCodeId = defaultCountry ? defaultCountry.country.id : residenceCountry?.id

    return {
      countryCodeId,
      countryCode: countryPhoneCode,
      number: '',
      isPrimary: true,
    }
  }

  const initialCountry = useMemo((): CountryPreferenceStrippedDto | undefined => {
    const residenceCountry = account?.addresses.find((x) => x.isPrimary)?.country
    return countries.find((country) => country.country.name === residenceCountry?.name)
  }, [account?.addresses, countries])

  const [values, setValues] = useState<PhoneNumberAddValues>(() => getInitialValues())

  const [phoneNumberError, setPhoneNumberError] = useState<string>()

  useEffect(() => {
    setValues(getInitialValues())
    setPhoneNumberError(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, countries])

  const validateSync = (v: PhoneNumberAddValues) => {
    const errors: { countryCode?: string; number?: string } = {}

    if (!v.countryCode) {
      errors.countryCode = t('Validation.Required')
    }

    if (!v.number) {
      errors.number = t('Validation.Required')
    } else if (v.number.toString().includes('.')) {
      errors.number = t('Validation.Invalid phone number. Numbers from 0 to 9.')
    }

    return errors
  }

  const syncErrors = validateSync(values)
  const isPhoneNumberReadyForValidation =
    !syncErrors.countryCode && !syncErrors.number && !!values.number

  const { isValid: isPhoneNumberValid, isInvalid: isPhoneNumberInvalid } =
    useClientPhoneNumberValidation({
      countryCode: values.countryCode ?? '',
      phoneNumber: values.number ?? '',
      enabled: isPhoneNumberReadyForValidation,
    })
  const phoneNumberErrorMessage = isPhoneNumberInvalid
    ? t('Sign up.Please check your phone number')
    : phoneNumberError

  const handleOnChangeOption = (country: CountryPreferenceStrippedDto) => {
    setValues((prev) => ({
      ...prev,
      countryCode: country.phoneCode,
      countryCodeId: country.country.id,
    }))
    setPhoneNumberError(undefined)
  }

  const handleChangePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    setValues((prev) => ({
      ...prev,
      number: value,
    }))

    setPhoneNumberError(undefined)
  }

  const handleTogglePrimary = (val: boolean) => {
    setValues((prev) => ({
      ...prev,
      isPrimary: val,
    }))
  }

  const handleConfirm = () => {
    const syncErrors = validateSync(values)

    if (syncErrors.countryCode || syncErrors.number || !isPhoneNumberValid) {
      setPhoneNumberError(
        phoneNumberErrorMessage ?? syncErrors.number ?? syncErrors.countryCode ?? undefined
      )
      return
    }

    onConfirm(values)
    close()
  }

  return (
    <Modal>
      <Modal.Header>{t('PhoneNumbers.Add New Phone Number')}</Modal.Header>
      <Modal.Body>
        <PhoneNumberInput
          name='phoneNumber'
          handleChangePhoneNumber={handleChangePhoneNumber}
          values={values.number ?? ''}
          countryCode={values.countryCode ?? ''}
          countries={countries}
          initialCountry={initialCountry}
          handleOnChangeOption={handleOnChangeOption}
          phoneNumberError={phoneNumberErrorMessage}
        />
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Top>
          <Checkbox checked={values.isPrimary} onChange={handleTogglePrimary}>
            {t('PhoneNumbers.Set as primary number')}
          </Checkbox>
        </Modal.Footer.Top>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary onClick={handleConfirm} disabled={!isPhoneNumberValid}>
          {isAccountActivatedStatus(account) ? t('PhoneNumbers.Confirm & Verify') : t('Confirm')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
